import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PortfolioMovementService, EnrollmentWithdrawalInfo, ReverseWithdrawalRequest, PortfolioMovementResult } from '../../../services/portfolio-movement.service';
import { ParticipantService } from '../../../services/participant.service';
import { ProjectService } from '../../../services/project.service';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-reversal-wizard',
  templateUrl: './reversal-wizard.component.html',
  styleUrls: ['./reversal-wizard.component.scss']
})
export class ReversalWizardComponent implements OnInit {
  // Exponer Math para el template
  Math = Math;
  
  currentStep = 1;
  
  // Datos del wizard
  participants: any[] = [];
  selectedParticipantId: number | null = null;
  withdrawnEnrollments: EnrollmentWithdrawalInfo[] = [];
  selectedEnrollment: EnrollmentWithdrawalInfo | null = null;
  projects: any[] = [];
  
  // Forms
  searchForm: FormGroup;
  destinationForm: FormGroup;
  valueForm: FormGroup;
  confirmForm: FormGroup;
  
  // Plan de pagos personalizado
  newPaymentPlan: { installmentNumber: number; dueDate: Date; amount: number; }[] = [];
  
  // Modal de refinanciación
  showRefinanceModal = false;
  refinanceInstallments = 2;
  minInstallments = 1;
  maxInstallments = 24;
  
  // UI states
  isLoading = false;
  isSearching = false;
  isSubmitting = false;
  searchPerformed = false;
  
  // 🆕 Resultado de la operación (para paso final)
  operationResult: PortfolioMovementResult | null = null;
  operationSuccess = false;
  operationError = '';
  
  // Preselección
  preselectedEnrollmentId: number | null = null;

  constructor(
    private fb: FormBuilder,
    private portfolioService: PortfolioMovementService,
    private participantService: ParticipantService,
    private projectService: ProjectService,
    private toastService: ToastService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.searchForm = this.fb.group({
      searchTerm: ['', Validators.required]
    });
    
    this.destinationForm = this.fb.group({
      continueInSameProject: [true],
      newProjectId: [null]
    });
    
    this.valueForm = this.fb.group({
      newTotalValueCOP: [0, [Validators.min(0)]],
      newTotalValueUSD: [0, [Validators.min(0)]],
      currencyId: [1]
    });
    
    this.confirmForm = this.fb.group({
      reason: ['', [Validators.required, Validators.minLength(10)]],
      notes: ['']
    });
  }

  // Total de pasos dinámico basado en si se continúa en el mismo proyecto o no
  get totalSteps(): number {
    const continueInSame = this.destinationForm.get('continueInSameProject')?.value;
    return continueInSame ? 5 : 7; // 7 pasos cuando hay nuevo proyecto (incluye valor y plan)
  }

  ngOnInit(): void {
    this.loadProjects();
    
    this.route.queryParams.subscribe(params => {
      if (params['enrollmentId']) {
        this.preselectedEnrollmentId = +params['enrollmentId'];
        this.loadEnrollmentById(this.preselectedEnrollmentId);
      }
    });

    // Validación dinámica del nuevo proyecto
    this.destinationForm.get('continueInSameProject')?.valueChanges.subscribe(value => {
      const newProjectControl = this.destinationForm.get('newProjectId');
      if (value) {
        newProjectControl?.clearValidators();
      } else {
        newProjectControl?.setValidators([Validators.required]);
      }
      newProjectControl?.updateValueAndValidity();
    });
  }

  // Helper para extraer datos de estructura paginada o array directo
  private extractData(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (response?.data) {
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    }
    return [];
  }

  loadProjects(): void {
    this.projectService.getAll().subscribe({
      next: (response) => {
        this.projects = this.extractData(response);
      },
      error: () => {
        this.toastService.showToast('error', 'Error al cargar proyectos');
      }
    });
  }

  searchParticipant(): void {
    if (this.searchForm.invalid) return;
    
    this.isSearching = true;
    this.searchPerformed = true;
    const term = this.searchForm.get('searchTerm')?.value;
    
    this.participantService.getAllFiltered(term).subscribe({
      next: (response) => {
        this.participants = this.extractData(response);
        this.isSearching = false;
      },
      error: () => {
        this.toastService.showToast('error', 'Error al buscar participantes');
        this.isSearching = false;
      }
    });
  }

  selectParticipant(participant: any): void {
    this.selectedParticipantId = participant.id;
    this.loadWithdrawnEnrollments(participant.id);
  }

  loadWithdrawnEnrollments(participantId: number): void {
    this.isLoading = true;
    this.portfolioService.getWithdrawnEnrollmentsByParticipant(participantId).subscribe({
      next: (response) => {
        this.withdrawnEnrollments = this.extractData(response);
        this.isLoading = false;
        if (this.withdrawnEnrollments.length === 0) {
          this.toastService.showToast('warning', 'Este participante no tiene inscripciones retiradas');
        }
      },
      error: () => {
        this.toastService.showToast('error', 'Error al cargar inscripciones');
        this.isLoading = false;
      }
    });
  }

  loadEnrollmentById(enrollmentId: number): void {
    this.isLoading = true;
    this.portfolioService.getEnrollmentWithdrawalInfo(enrollmentId).subscribe({
      next: (response) => {
        const enrollment = response.data || response;
        if (enrollment && enrollment.canBeReversed) {
          this.selectedEnrollment = enrollment;
          this.currentStep = 2;
        } else {
          this.toastService.showToast('error', 'Esta inscripción no puede ser revertida');
          this.router.navigate(['/enrollments']);
        }
        this.isLoading = false;
      },
      error: () => {
        this.toastService.showToast('error', 'Error al cargar inscripción');
        this.isLoading = false;
      }
    });
  }

  selectEnrollment(enrollment: EnrollmentWithdrawalInfo): void {
    if (!enrollment.canBeReversed) {
      this.toastService.showToast('warning', 'Esta inscripción no puede ser revertida');
      return;
    }
    this.selectedEnrollment = enrollment;
    this.currentStep = 2;
  }

  get punishedInstallments() {
    return this.selectedEnrollment?.pendingPaymentPlans.filter(pp => pp.isPunished) || [];
  }

  get totalPunishedAmount(): number {
    return this.punishedInstallments.reduce((sum, pp) => sum + pp.pendingAmount, 0);
  }

  goToStep(step: number): void {
    if (step < this.currentStep) {
      this.currentStep = step;
    }
  }

  nextStep(): void {
    const continueInSame = this.destinationForm.get('continueInSameProject')?.value;
    
    if (this.currentStep === 1 && this.selectedEnrollment) {
      this.currentStep = 2;
    } else if (this.currentStep === 2) {
      this.currentStep = 3;
    } else if (this.currentStep === 3 && this.isDestinationValid()) {
      if (continueInSame) {
        // Si continúa en el mismo proyecto, saltar a confirmación
        this.currentStep = 4;
      } else {
        // Si es nuevo proyecto, ir a paso de valor
        this.initializeValueFromPunished();
        this.currentStep = 4;
      }
    } else if (this.currentStep === 4 && !continueInSame) {
      // Paso de valor -> Paso de plan de pagos
      this.generatePaymentPlan(2); // Plan inicial con 2 cuotas
      this.currentStep = 5;
    } else if (this.currentStep === 5 && !continueInSame && this.isPlanValid) {
      // Paso de plan -> Confirmación
      this.currentStep = 6;
    } else if ((this.currentStep === 4 && continueInSame) || (this.currentStep === 6 && !continueInSame)) {
      // Ya manejado por submitReversal
    }
  }

  prevStep(): void {
    if (this.currentStep > 1) {
      const continueInSame = this.destinationForm.get('continueInSameProject')?.value;
      
      // Ajustar navegación hacia atrás
      if (!continueInSame && this.currentStep === 6) {
        this.currentStep = 5; // De confirmación a plan
      } else if (!continueInSame && this.currentStep === 5) {
        this.currentStep = 4; // De plan a valor
      } else {
        this.currentStep--;
      }
    }
  }

  // Inicializar valores desde las cuotas castigadas
  initializeValueFromPunished(): void {
    const total = this.totalPunishedAmount;
    this.valueForm.patchValue({
      newTotalValueCOP: total,
      newTotalValueUSD: 0,
      currencyId: 1
    });
  }

  // Autollenar desde el proyecto seleccionado
  autofillFromProject(): void {
    const project = this.getSelectedProject();
    if (project) {
      this.valueForm.patchValue({
        newTotalValueCOP: project.inscriptionAmountCOP || project.inscriptionAmount || 0,
        newTotalValueUSD: project.inscriptionAmountUSD || 0
      });
    }
  }

  // Modal de refinanciación
  openRefinanceModal(): void {
    this.refinanceInstallments = this.newPaymentPlan.length || 2;
    this.showRefinanceModal = true;
  }

  closeRefinanceModal(): void {
    this.showRefinanceModal = false;
  }

  confirmRefinance(): void {
    this.generatePaymentPlan(this.refinanceInstallments);
    this.closeRefinanceModal();
  }

  // Generar plan de pagos automático
  generatePaymentPlan(installmentsCount: number): void {
    const currencyId = this.valueForm.get('currencyId')?.value || 1;
    const totalValue = currencyId === 1 
      ? this.parseAmount(this.valueForm.get('newTotalValueCOP')?.value)
      : this.parseAmount(this.valueForm.get('newTotalValueUSD')?.value);
    
    const installmentAmount = totalValue / installmentsCount;
    this.newPaymentPlan = [];
    
    for (let i = 1; i <= installmentsCount; i++) {
      this.newPaymentPlan.push({
        installmentNumber: i,
        dueDate: this.calculateNextDueDate(i),
        amount: i === installmentsCount 
          ? totalValue - (installmentAmount * (installmentsCount - 1)) // Última cuota ajusta el residuo
          : Math.floor(installmentAmount)
      });
    }
  }

  calculateNextDueDate(installmentNumber: number): Date {
    const today = new Date();
    const dueDate = new Date(today.getFullYear(), today.getMonth() + installmentNumber, 15);
    return dueDate;
  }

  // Agregar/eliminar cuotas manualmente
  addInstallment(): void {
    const nextNumber = this.newPaymentPlan.length + 1;
    this.newPaymentPlan.push({
      installmentNumber: nextNumber,
      dueDate: this.calculateNextDueDate(nextNumber),
      amount: 0
    });
  }

  removeInstallment(index: number): void {
    if (this.newPaymentPlan.length > 1) {
      this.newPaymentPlan.splice(index, 1);
      // Renumerar cuotas
      this.newPaymentPlan.forEach((plan, i) => {
        plan.installmentNumber = i + 1;
      });
    }
  }

  // Validación del plan
  get isPlanValid(): boolean {
    if (this.newPaymentPlan.length === 0) return false;
    const totalPlan = this.currentPlanTotal;
    const expectedTotal = this.getActiveTotal();
    const tolerance = 1; // Tolerancia de $1 por redondeos
    return Math.abs(totalPlan - expectedTotal) <= tolerance;
  }

  get currentPlanTotal(): number {
    return this.newPaymentPlan.reduce((sum, plan) => sum + this.parseAmount(plan.amount), 0);
  }

  getActiveTotal(): number {
    const currencyId = this.valueForm.get('currencyId')?.value || 1;
    return currencyId === 1 
      ? this.parseAmount(this.valueForm.get('newTotalValueCOP')?.value)
      : this.parseAmount(this.valueForm.get('newTotalValueUSD')?.value);
  }

  getEstimatedInstallmentAmount(): number {
    const count = this.newPaymentPlan.length || 2;
    return this.getActiveTotal() / count;
  }

  parseAmount(value: any): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      // Remover formato de moneda (puntos, comas, símbolos)
      const cleaned = value.replace(/[^\d.-]/g, '');
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  }

  formatCurrency(value: number, currency: string = 'COP'): string {
    if (currency === 'USD') {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
    }
    return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(value);
  }

  isDestinationValid(): boolean {
    const continueInSame = this.destinationForm.get('continueInSameProject')?.value;
    if (continueInSame) return true;
    return !!this.destinationForm.get('newProjectId')?.value;
  }

  submitReversal(): void {
    if (this.confirmForm.invalid || !this.selectedEnrollment || !this.isDestinationValid() || this.isSubmitting) return;
    
    const continueInSame = this.destinationForm.get('continueInSameProject')?.value;
    
    this.isSubmitting = true;
    const request: ReverseWithdrawalRequest = {
      enrollmentId: this.selectedEnrollment.enrollmentId,
      continueInSameProject: continueInSame,
      newProjectId: this.destinationForm.get('newProjectId')?.value,
      reason: this.confirmForm.get('reason')?.value,
      notes: this.confirmForm.get('notes')?.value
    };

    // Agregar valores personalizados si es un nuevo proyecto
    if (!continueInSame && this.newPaymentPlan.length > 0) {
      request.newTotalValueCOP = this.parseAmount(this.valueForm.get('newTotalValueCOP')?.value);
      request.newTotalValueUSD = this.parseAmount(this.valueForm.get('newTotalValueUSD')?.value);
      request.currencyId = this.valueForm.get('currencyId')?.value;
      request.newPaymentPlan = this.newPaymentPlan.map(p => ({
        installmentNumber: p.installmentNumber,
        dueDate: p.dueDate,
        amount: this.parseAmount(p.amount)
      }));
    }

    const resultStep = continueInSame ? 5 : 7;

    this.portfolioService.reverseWithdrawal(request).subscribe({
      next: (response) => {
        const result = response.data || response;
        this.operationResult = result;
        this.isSubmitting = false;
        
        if (result.success) {
          this.operationSuccess = true;
          this.currentStep = resultStep;
        } else {
          this.operationSuccess = false;
          this.operationError = result.message || 'Error al procesar reversión';
          this.currentStep = resultStep;
        }
      },
      error: () => {
        this.operationSuccess = false;
        this.operationError = 'Error al procesar reversión. Por favor, intente nuevamente.';
        this.currentStep = resultStep;
        this.isSubmitting = false;
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/enrollments']);
  }

  getSelectedProject(): any {
    const projectId = this.destinationForm.get('newProjectId')?.value;
    return this.projects.find(p => p.id === projectId);
  }

  // 🆕 Métodos para el paso final
  viewEnrollment(): void {
    const targetEnrollment = this.operationResult?.newEnrollmentId || this.selectedEnrollment?.enrollmentId;
    if (targetEnrollment) {
      this.router.navigate(['/enrollments/view', targetEnrollment]);
    }
  }

  viewOriginalEnrollment(): void {
    if (this.selectedEnrollment) {
      this.router.navigate(['/enrollments/view', this.selectedEnrollment.enrollmentId]);
    }
  }

  goToEnrollmentsList(): void {
    this.router.navigate(['/enrollments']);
  }

  startNewReversal(): void {
    this.currentStep = 1;
    this.selectedParticipantId = null;
    this.selectedEnrollment = null;
    this.withdrawnEnrollments = [];
    this.participants = [];
    this.searchPerformed = false;
    this.operationResult = null;
    this.operationSuccess = false;
    this.operationError = '';
    this.newPaymentPlan = [];
    this.searchForm.reset();
    this.destinationForm.reset();
    this.destinationForm.patchValue({ continueInSameProject: true });
    this.valueForm.reset();
    this.valueForm.patchValue({ newTotalValueCOP: 0, newTotalValueUSD: 0, currencyId: 1 });
    this.confirmForm.reset();
  }

  // Obtener el paso de confirmación según el flujo
  get confirmationStep(): number {
    const continueInSame = this.destinationForm.get('continueInSameProject')?.value;
    return continueInSame ? 4 : 6;
  }

  // Obtener el paso de resultado según el flujo
  get resultStep(): number {
    const continueInSame = this.destinationForm.get('continueInSameProject')?.value;
    return continueInSame ? 5 : 7;
  }
}
